import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { marketsForScope } from '../dataProvider/types';
import type { CmsPage } from '../dataProvider';
import { KIND_LABEL } from './cmsModel';

/**
 * CMS pages list (D-cms-pages-core). The conventional rich-text marketing pages by
 * title, slug, kind, market and status. OPERATIONAL market-scoped: the switcher governs
 * which pages show (the active markets PLUS the all-market pages, the RuleMarket idiom).
 * ROLE-gated to editor and super_admin (a capability read-gate would leak via a viewer's
 * '*:read'). Authoring a new page gates cms:author; publishing is taken from the edit
 * view. SEPARATE from the page-schema pages (rich text, not blocks).
 */

const cmsFilters = [
  <TextInput key="q" source="q" label="Search title" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'draft', name: 'Draft' },
      { id: 'published', name: 'Published' },
    ]}
  />,
];

const CmsPageListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New page" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const CmsPageListView = () => {
  const { activeRegion } = useRegion();
  // The active markets PLUS the all-market pages (a both page shows in every scope).
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  return (
    <List
      resource="cmsPages"
      title="CMS pages"
      filter={{ market: markets }}
      filters={cmsFilters}
      actions={<CmsPageListActions />}
      sort={{ field: 'title', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="title" label="Title" />
        <TextField source="slug" label="Slug" />
        <FunctionField
          label="Kind"
          render={(r: CmsPage) => KIND_LABEL[r.kind]}
        />
        <FunctionField
          label="Market"
          render={(r: CmsPage) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Status"
          render={(r: CmsPage) => (
            <Chip
              size="small"
              variant={r.status === 'published' ? 'filled' : 'outlined'}
              color={r.status === 'published' ? 'success' : 'default'}
              label={r.status === 'published' ? 'Published' : 'Draft'}
              data-testid={`cms-status-${r.id}`}
            />
          )}
        />
        <FunctionField
          label="Published"
          render={(r: CmsPage) =>
            r.publishedAt?.slice(0, 10) ?? 'not published'
          }
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const CmsPageList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <CmsPageListView />
  </RequireAccess>
);
